import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
  CircularProgress
} from "@mui/material";
import { ChangeEvent, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import SwitchToggle from "../../components/SwitchToggle";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import {
  useGetBrandsQuery,
  useGetModelsQuery,
  useGetCategoriesQuery,
  useGetCategoryDetailsQuery,
  useAddCarAccessoryMutation,
  useUpdateCarAccessoryMutation,
  useGetCarAccessoryDetailsQuery,
  useUploadMediaMutation,
  useGetShowroomsQuery
} from "../../service/features/api";
import { VEHICLE_TYPE, VEHICLE_CONDITION } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";

const AddEditCarAccessory = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const location = useLocation();
  const defaultType = location.state?.defaultType;

  const [showroomImages, setShowroomImages] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<any>(null);
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [modelId, setModelId] = useState("");
  const [conditionId, setConditionId] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">(defaultType || "");
  const [name, setName] = useState("");
  const [actualPrice, setActualPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [dealer, setDealer] = useState("");
  const [errors, setErrors] = useState<any>({});

  const [addCarAccessory, { isLoading: isAdding }] = useAddCarAccessoryMutation();
  const [updateCarAccessory, { isLoading: isUpdating }] = useUpdateCarAccessoryMutation();
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  // Fetch details for edit mode
  const { data: accessoryDetails, isLoading: isFetchingDetails } = useGetCarAccessoryDetailsQuery(id, {
    skip: !isEditMode,
    refetchOnMountOrArgChange: true
  });

  useEffect(() => {
    if (isEditMode && accessoryDetails?.data) {
      const data = accessoryDetails.data;
      setName(data.name || "");

      // Handle potentially populated objects or direct IDs
      setCategoryId(data.categoryId?._id || data.categoryId || "");
      setSubCategoryId(data.subCategoryId?._id || data.subCategoryId || "");
      setBrandId(data.brand?._id || data.brand || "");
      setModelId(data.model?._id || data.model || "");
      setConditionId(data.condition?._id || data.condition || "");

      setActualPrice(data.actualPrice || "");
      setDiscountPrice(data.discountPrice || "");
      setShortDesc(data.shortDescription || "");
      setIsFeatured(data.isFeatured || false);
      setConditionId(data.condition || "");
      const vType = data.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setShowroomImages(data.images || []);
      setDealer(data.dealer?._id || data.dealer || "");

      if (data.manufacturingYear) {
        setSelectedYear(dayjs().year(data.manufacturingYear));
      }
    }
  }, [accessoryDetails, isEditMode]);


  const handleImageUpload = async (file: File) => {
    if (showroomImages.length >= 20) {
      toast.error("You can upload a maximum of 20 images");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await uploadMedia(formData).unwrap();
      const imageUrl = res?.data?.url || res?.url || res?.data;
      if (imageUrl) {
        setShowroomImages([...showroomImages, imageUrl]);
        toast.success("Image uploaded");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Upload failed");
    }
  };

  const validate = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) tempErrors.name = "Name is required";
    if (!dealer) tempErrors.dealer = "Dealer is required";
    if (!categoryId) tempErrors.categoryId = "Category is required";
    // if (!subCategoryId) tempErrors.subCategoryId = "Sub Category is required"; 
    if (!conditionId) tempErrors.conditionId = "Condition is required";
    if (!actualPrice) {
      tempErrors.actualPrice = "Actual Price is required";
    } else if (isNaN(Number(actualPrice))) {
      tempErrors.actualPrice = "Please enter a valid price";
    } else if (Number(actualPrice) < 1) {
      tempErrors.actualPrice = "Actual Price must be at least 1.";
    }

    if (!discountPrice) {
      tempErrors.discountPrice = "Discounted Price is required";
    } else if (isNaN(Number(discountPrice))) {
      tempErrors.discountPrice = "Please enter a valid price";
    } else if (Number(discountPrice) < 1) {
      tempErrors.discountPrice = "Discounted Price must be at least 1.";
    } else if (Number(discountPrice) >= Number(actualPrice)) {
      tempErrors.discountPrice = "Discounted Price cannot be greater than or equal to Actual Price.";
    }
    if (!shortDesc.trim()) tempErrors.shortDesc = "Description is required";
    if (!brandId) tempErrors.brandId = "Brand is required";
    if (!modelId) tempErrors.modelId = "Model is required";
    if (!selectedVehicleType) tempErrors.vehicleType = "Vehicle Type is required";
    if (!selectedYear) tempErrors.selectedYear = "Year is required";

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      isValid = false;
      toast.error("Please fill all required fields");
    } else {
      setErrors({});
    }
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload: any = {
        categoryId,
        subCategoryId: subCategoryId || undefined,
        name,
        images: showroomImages,
        condition: Number(conditionId),
        actualPrice: Number(actualPrice),
        discountPrice: Number(discountPrice),
        fullDescription: shortDesc,
        shortDescription: shortDesc,
        brand: brandId,
        model: modelId,
        type: Number(selectedVehicleType),
        dealer,
        manufacturingYear: selectedYear ? selectedYear.year() : undefined,
        isFeatured
      };

      if (isEditMode) {
        const res = await updateCarAccessory({ id, ...payload }).unwrap();
        toast.success(res?.message || "Accessory updated successfully");
      } else {
        const res = await addCarAccessory(payload).unwrap();
        toast.success(res?.message || "Accessory added successfully");
      }
      navigate("/manage-car-accessories");
    } catch (err: any) {
      toast.error(err?.data?.message || `Failed to ${isEditMode ? "update" : "add"} Accessory`);
      console.error(err);
    }
  };

  // Queries
  const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100, type: selectedVehicleType });
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, type: selectedVehicleType });
  const { data: categoryDetails, isLoading: isSubCatLoading } = useGetCategoryDetailsQuery(
    categoryId,
    { skip: !categoryId }
  );
  // const { data: conditionsData } = useGetConditionsQuery({ page: 1, limit: 100 });
  const { data: modelsData, isLoading: isModelLoading } = useGetModelsQuery(
    { page: 1, limit: 100, type: selectedVehicleType, categoryId: categoryId, brandId: brandId }
  );
  const { data: showroomsData } = useGetShowroomsQuery({ page: 1, limit: 100 });

  const brands = brandsData?.data?.data || [];
  const categories = categoriesData?.data?.data || [];
  const subCategories = categoryDetails?.data?.subCategories || [];
  // const conditions = conditionsData?.data?.data || [];
  const models = modelsData?.data?.data || [];
  const showrooms = showroomsData?.data?.data || [];

  if (isEditMode && isFetchingDetails) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title={`${!isEditMode ? "Add" : "Edit"} Accessory`}
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Listed Accessories", onClick: () => navigate("/manage-car-accessories") },
          { label: `${!isEditMode ? "Add" : "Edit"} Accessory` },
        ]}
      />

      <div className="form">

        {/* ================= Spare Part Detail ================= */}
        <div className="cards">
          <div className="table_header mb_20">
            <h2>Accessory Detail</h2>
          </div>

          <div className="gap_p">

            {/* Image Upload */}
            <div className="control_group w_100">
              <label>Upload Images</label>
              <div className="gap_p">
                {/* Upload Button — always first/leftmost */}
                {showroomImages.length < 20 && (
                  <div className="w_25">
                    <label className="upload_image no_radius v2" style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
                      <Input
                        sx={{ display: "none" }}
                        type="file"
                        inputProps={{ accept: "image/*" }}
                        disabled={isUploading}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const files = event.target.files;
                          if (files && files[0]) {
                            handleImageUpload(files[0]);
                            event.target.value = ""; // Reset input
                          }
                        }}
                      />
                      <span className="upload_image_holder">
                        {isUploading ? (
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                            <CircularProgress size={24} />
                          </div>
                        ) : (
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt=""
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt=""
                              />
                            </span>
                          </figure>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                {/* Image Previews — rendered after the upload button */}
                {showroomImages.map((img, index) => (
                  <div className="w_25" key={index}>
                    <div className="upload_image no_radius v2">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={img} />
                        </figure>
                        <CloseIcon
                          className="remove_img"
                          onClick={() => {
                            const newImages = [...showroomImages];
                            newImages.splice(index, 1);
                            setShowroomImages(newImages);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Showroom */}
            <div className="control_group w_100">
              <label>Select Showroom</label>
              <Select
                fullWidth
                hiddenLabel
                value={dealer}
                displayEmpty
                onChange={(e) => {
                  setDealer(e.target.value);
                  if (errors.dealer) setErrors({ ...errors, dealer: "" });
                }}
                error={!!errors.dealer}
              >
                <MenuItem value="" disabled>Select Showroom</MenuItem>
                {showrooms.map((showroom: any) => (
                  <MenuItem key={showroom._id} value={showroom._id}>
                    {showroom.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.dealer && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.dealer}</p>}
            </div>

            {/* Accessory Name */}
            <div className="control_group w_100">
              <label>Accessory Name</label>
              <TextField
                placeholder="Enter Accessory Name"
                hiddenLabel
                fullWidth
                value={name}
                onChange={(e) => {
                  if (e.target.value.length <= 40) setName(e.target.value);
                }}
                error={!!errors.name}
                helperText={errors.name}
              />
            </div>

            <div className="control_group w_33">
              <label>Brand</label>
              <Select
                fullWidth
                hiddenLabel
                value={brandId}
                displayEmpty
                onChange={(e) => {
                  setBrandId(e.target.value);
                }}
                error={!!errors.brandId}
              >
                <MenuItem value="" disabled>Select Brand</MenuItem>
                {brands.map((brand: any) => (
                  <MenuItem key={brand._id} value={brand._id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.brandId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.brandId}</p>}
            </div>

            <div className="control_group w_33">
              <label>Model</label>
              <Select
                fullWidth
                hiddenLabel
                value={modelId}
                displayEmpty
                onChange={(e) => setModelId(e.target.value)}
              >
                <MenuItem value="" disabled>Select Model</MenuItem>
                {isModelLoading ? (
                  <MenuItem value="" disabled>Loading...</MenuItem>
                ) : models.length > 0 ? (
                  models.map((model: any) => (
                    <MenuItem key={model._id} value={model._id}>
                      {model.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>No Models Found</MenuItem>
                )}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>Year</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={['year']}
                  value={selectedYear}
                  onChange={(newValue) => setSelectedYear(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "Select Year",
                      hiddenLabel: true,
                      error: !!errors.selectedYear,
                      helperText: errors.selectedYear
                    }
                  }}
                  disableFuture
                />
              </LocalizationProvider>
            </div>

            <div className="control_group w_33">
              <label htmlFor="VehicleType">Vehicle Type</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                disabled={!!defaultType && !isEditMode}
                value={selectedVehicleType}
                onChange={(event) => {
                  setSelectedVehicleType(event.target.value as number);
                  if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
                }}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.vehicleType}</p>}
            </div>



            {/* Category */}
            <div className="control_group w_33">
              <label>Category</label>
              <Select
                fullWidth
                hiddenLabel
                value={categoryId}
                displayEmpty
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setSubCategoryId("");
                }}
              >
                <MenuItem value="" disabled>Select Category</MenuItem>
                {categories.map((cat: any) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* SubCategory */}
            <div className="control_group w_33">
              <label>Sub Category</label>
              <Select
                fullWidth
                hiddenLabel
                value={subCategoryId}
                displayEmpty
                onChange={(e) => setSubCategoryId(e.target.value)}
              >
                <MenuItem value="" disabled>{categoryId ? "Select Sub Category" : "Select Category First"}</MenuItem>
                {isSubCatLoading ? (
                  <MenuItem value="" disabled>Loading...</MenuItem>
                ) : subCategories.length > 0 ? (
                  subCategories.map((sub: any) => (
                    <MenuItem key={sub._id} value={sub._id}>
                      {sub.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>No Sub Categories Found</MenuItem>
                )}
              </Select>
            </div>



            {/* Condition */}
            <div className="control_group w_33">
              <label>Condition <span className="c_danger">*</span></label>
              <Select
                fullWidth
                hiddenLabel
                value={conditionId}
                displayEmpty
                onChange={(e) => {
                  setConditionId(e.target.value);
                  if (errors.conditionId) setErrors({ ...errors, conditionId: "" });
                }}
                error={!!errors.conditionId}
              >
                <MenuItem value="" disabled>Select Condition</MenuItem>
                {Object.keys(VEHICLE_CONDITION)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_CONDITION as any)[key]}>
                      {key.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
              </Select>
              {errors.conditionId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.conditionId}</p>}
            </div>

            {/* Prices */}
            <div className="control_group w_33">
              <div className="gap_p">
                <div className="control_group w_50">
                  <label>Actual Price</label>
                  <TextField
                    placeholder="Actual Price"
                    hiddenLabel
                    fullWidth
                    value={actualPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setActualPrice(val);
                        if (errors.actualPrice) setErrors({ ...errors, actualPrice: "" });
                      }
                    }}
                    error={!!errors.actualPrice}
                    helperText={errors.actualPrice}
                  />
                </div>

                <div className="control_group w_50">
                  <label>Discounted Price</label>
                  <TextField
                    placeholder="Discounted Price"
                    hiddenLabel
                    fullWidth
                    value={discountPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setDiscountPrice(val);
                        if (errors.discountPrice) setErrors({ ...errors, discountPrice: "" });
                      }
                    }}
                    error={!!errors.discountPrice}
                    helperText={errors.discountPrice}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label>Description</label>
              <TextField
                multiline
                rows={4}
                placeholder="Enter description"
                hiddenLabel
                fullWidth
                value={shortDesc}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.length === 1 && val === " ") val = "";
                  if (val.length <= 800) {
                    setShortDesc(val);
                  }
                }}
                inputProps={{ maxLength: 800 }}
                error={!!errors.shortDesc}
                helperText={errors.shortDesc}
              />
            </div>

            {/* Featured */}
            <div className="control_group flex w_100">
              <label>Is this Featured Accessory?</label>
              <SwitchToggle checked={isFeatured} onChange={() => setIsFeatured(!isFeatured)} />
            </div>
          </div>
        </div>



        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button className="btn btn_primary" onClick={handleSubmit} disabled={isAdding || isUpdating}>
            {isAdding || isUpdating ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddEditCarAccessory;